import { useCallback } from 'react';
import { ConfirmDestructiveActionModal } from '@cotera/client/app/components/app';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';

/**
 * Confirms deleting the picked dataset rows, resolving `true` to go ahead.
 *
 * Deleting rows persists a `delete_row` operation each and takes their values
 * with them, so it warrants the same guard dropping a column does. Rows are
 * picked by range, where a mis-click selects far more than intended, and the
 * count is the one thing worth naming: "delete 400 rows" is a different act
 * from "delete 4".
 */
export function useConfirmDeleteRows(): (rowCount: number) => Promise<boolean> {
  const modalManager = useModalManager((s) => s.actions);

  return useCallback(
    (rowCount: number) =>
      new Promise<boolean>((resolve) => {
        let settled = false;
        const settle = (confirmed: boolean): void => {
          if (settled) {
            return;
          }
          settled = true;
          resolve(confirmed);
        };

        const label =
          rowCount === 1 ? '1 row' : `${rowCount.toLocaleString()} rows`;

        modalManager.push({
          view: () => (
            <ConfirmDestructiveActionModal
              title={`Delete ${label}?`}
              subtitle="This removes them and every value in them from the dataset. It can't be recovered."
              confirmLabel={`Delete ${label}`}
              onConfirm={() => {
                settle(true);
                modalManager.pop();
              }}
              onCancel={() => {
                settle(false);
                modalManager.pop();
              }}
            />
          ),
          // Escape or a backdrop click leaves the rows alone.
          onClose: () => settle(false),
        });
      }),
    [modalManager]
  );
}
